import { supabase } from '../lib/supabase';

/**
 * Service for managing temporary public patient bookings
 * Handles non-authenticated user submissions and transfers to permanent tables
 */

/**
 * Create a temporary public patient booking
 * @param {Object} bookingData - Booking data from public form
 * @returns {Promise} Result with data or error
 */
export const createPublicPatientBooking = async (bookingData) => {
  try {
    const {
      psychologistId,
      fullName,
      whatsappNumber,
      email,
      dateOfBirth,
      appointmentDate,
      startTime,
      duration
    } = bookingData;

    // Calculate end time
    const [hours, minutes] = startTime?.split(':');
    const startDate = new Date();
    startDate?.setHours(parseInt(hours), parseInt(minutes), 0);
    const endDate = new Date(startDate.getTime() + duration * 60000);
    const endTime = `${endDate?.getHours()?.toString()?.padStart(2, '0')}:${endDate?.getMinutes()?.toString()?.padStart(2, '0')}:00`;

    const { data, error } = await supabase?.from('temp_public_patients')?.insert({
        psychologist_id: psychologistId,
        full_name: fullName,
        whatsapp_number: whatsappNumber,
        email: email || null,
        date_of_birth: dateOfBirth || null,
        appointment_date: appointmentDate,
        start_time: startTime,
        end_time: endTime,
        duration: duration
      })?.select()?.single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error creating public patient booking:', error);
    return { data: null, error };
  }
};

/**
 * Get temporary public patients for a psychologist
 * @param {string} psychologistId - Psychologist UUID
 * @param {Object} filters - Optional filters (transferred, date range, etc.)
 * @returns {Promise} Result with data or error
 */
export const getTempPublicPatients = async (psychologistId, filters = {}) => {
  try {
    let query = supabase?.from('temp_public_patients')?.select('*')?.eq('psychologist_id', psychologistId)?.order('appointment_date', { ascending: true })?.order('start_time', { ascending: true });

    // Apply filters
    if (filters?.transferred !== undefined) {
      query = query?.eq('transferred', filters?.transferred);
    }

    if (filters?.fromDate) {
      query = query?.gte('appointment_date', filters?.fromDate);
    }

    if (filters?.toDate) {
      query = query?.lte('appointment_date', filters?.toDate);
    }

    const { data, error } = await query;

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching temp public patients:', error);
    return { data: null, error };
  }
};

/**
 * Delete a temporary public patient
 * @param {string} tempPatientId - Temporary patient UUID
 * @returns {Promise} Result with success or error
 */
export const deleteTempPublicPatient = async (tempPatientId) => {
  try {
    const { error } = await supabase?.from('temp_public_patients')?.delete()?.eq('id', tempPatientId);

    if (error) throw error;
    return { data: true, error: null };
  } catch (error) {
    console.error('Error deleting temp public patient:', error);
    return { data: null, error };
  }
};

export const publicPatientService = {
  createPublicPatientBooking,
  getTempPublicPatients,
  deleteTempPublicPatient
};